import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { Input } from '../../../elements/input';
import { TextArea } from '../../../elements/text-area';
import { FormFooter } from '../../../elements/form-footer';
import { Area } from './types';

const validationSchema = Yup.object().shape({
    name: Yup.string(),
    description: Yup.string()
});

interface AreaFormProps {
    area: Partial<Area>;
    onSave: (values: any) => void;
    onCancel: () => void;
    onRadiusChange?: (radius: number) => void;
}

export const AreaForm = ({ area, onSave, onCancel, onRadiusChange }: AreaFormProps) => {
    const { t } = useTranslation() as { t: (key: string) => string };

    const initialValues = {
        id: area.id || undefined,
        name: area.name || '',
        description: area.description || '',
        center_lat: area.center_lat || 0,
        center_lon: area.center_lon || 0,
        radius: area.radius || 10
    };

    const handleSubmit = (values: any) => {
        const dataToSend = {
            ...values,
            name: values.name.trim() || undefined
        };
        onSave(dataToSend);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ values, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                                <span className="font-semibold text-gray-700">{t('common.label.latitud')}:</span>
                                <span className="ml-2 text-gray-900">{values.center_lat.toFixed(6)}</span>
                            </div>
                            <div>
                                <span className="font-semibold text-gray-700">{t('common.label.longitud')}:</span>
                                <span className="ml-2 text-gray-900">{values.center_lon.toFixed(6)}</span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <span className="font-semibold text-gray-700 text-sm">{t('common.label.radio')}:</span>
                            <input
                                type="range"
                                min="1"
                                max="500"
                                step="1"
                                value={values.radius}
                                onChange={(e) => {
    const newRadius = parseFloat(e.target.value);
    setFieldValue('radius', newRadius);
    if (onRadiusChange) {
        onRadiusChange(newRadius);
    }
}}
                                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                style={{
                                    background: `linear-gradient(to right, #006bc6 0%, #006bc6 ${((values.radius - 1) / 499) * 100}%, #e5e7eb ${((values.radius - 1) / 499) * 100}%, #e5e7eb 100%)`
                                }}
                            />
                            <span className="text-sm font-bold text-[#006bc6] min-w-[50px] text-right">
                                {values.radius} km
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('common.label.nombre')}
                            <span className="text-xs text-gray-500 ml-2">({t('common.label.opcional')})</span>
                        </label>
                        <Input
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={(item) => setFieldValue('name', item.name)}
                            placeholder={t('common.placeholder.nombre-area')}
                        />
                        <p className="mt-1 text-xs text-gray-500">{t('common.label.nombre-generado-automatico')}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('common.form-label.descripcion')}
                            <span className="text-xs text-gray-500 ml-2">({t('common.label.opcional')})</span>
                        </label>
                        <TextArea
                            name="description"
                            value={values.description}
                            onChange={(item) => setFieldValue('description', item.description)}
                            placeholder={t('common.placeholder.descripcion-area')}
                        />
                    </div>

                    <FormFooter
                        close={onCancel}
                        doSubmit={() => handleSubmit()}
                    />
                </form>
            )}
        </Formik>
    );
};
